import { PaymentDTO, Permission, Res } from 'core/api';
import { Result, SessionProvider } from 'core/infrastructure';

import { PaymentDirection, TransactionType, paymentRequiresCardProcessor } from './ExternalPaymentsService.Common';
import { WarnBeforeUndoingScreen } from './ExternalPaymentsService.WarnBeforeUndoingScreen';
import { ConfirmCancellationScreen } from './ExternalPaymentsService.ConfimCancellationScreen';
import { TransactionList } from './ExternalPaymentService.TransactionList';
import { Message } from 'core/ui-next/modal';
import { Currency, Math } from 'core/utils';
import * as TransactionUtils from './TransactionUtils';

export default class ExternalPaymentsService {

	static forPay(type: 'Pay' | 'PrePay' | 'AddTip' | 'NoDocument', ticketGlobalId: Nullable<string>): ExternalPaymentsService {
		return new ExternalPaymentsService('PAY', type, ticketGlobalId);
	}

	static forRefund(type: 'Refund' | 'PreRefund' | 'RemoveTip' | 'NoDocument', ticketGlobalId: Nullable<string>): ExternalPaymentsService {
		return new ExternalPaymentsService('REFUND', type, ticketGlobalId);
	}

	private constructor(
		private readonly direction: PaymentDirection,
		private readonly type: TransactionType,
		private readonly ticketGlobalId: Nullable<string>,
		private readonly payments: PaymentDTO[] = [],
		private readonly transactions: TransactionList = new TransactionList(type, direction)
	) {
	}

	async register(payment: PaymentDTO): Promise<Result> {

		if (!payment.IsCash && this.type === 'NoDocument')
			throw new Error('Cuando el tipo es NoDocument, sólo se pueden gestionar movimientos de efectivo. Se usa para casos en que hay que interactuar con un CashControl sin que esté involucrado un documento. Por ejemplo, añadir/quitar saldo de repartidores o movimientos de caja.');

		if (paymentRequiresCardProcessor(payment)) {

			if (this.direction === 'REFUND' && !SessionProvider.hasPermission(Permission.CardProcessor_AllowUnreferencedRefunds)) {
				await Message.show(Res.Msg.MSG_PERMISSION_DENIED_FOR(Permission.toString(Permission.CardProcessor_AllowUnreferencedRefunds)));
				return Result.error();
			}

			// Pido propina cuando no me la han prefijado antes y debo pedirla
			const askGratuity = payment.RegisterTip && payment.TipAmount === 0 && SessionProvider.deviceAskGratuity() && SessionProvider.allowSuggestedTipSettings() && this.direction === 'PAY';
			const result = await this.transactions.addCardTransaction(this.ticketGlobalId, { type: this.type, amount: this.getAmountToProcess(payment), askGratuity });
			if (!result.ok)
				return Result.error();

			payment.CardTransaction = result.value;
			// Si el pago viene con propina prefijada, es la que vamos ausar
			payment.TipAmount = payment.TipAmount === 0 ? TransactionUtils.getTip(result.value.XmlDetails) : payment.TipAmount;
		}

		this.payments.push(payment);
		return Result.ok();
	}

	async unregister(payment: PaymentDTO): Promise<Result> {

		if (paymentRequiresCardProcessor(payment)) {
			if (!this.ticketGlobalId)
				throw new Error('No se pueden procesar pagos con tarjeta si no hay un ticketGlobalId asignado');

			const result = await this.transactions.removeCardTransaction(this.getAmountToProcess(payment), payment.CardTransaction.XmlDetails);
			if (!result.ok)
				return Result.error();
		}

		this.payments.remove(payment);

		return Result.ok();
	}

	async undoAll(): Promise<void> {

		if (this.transactions.hasExternalTransactions()) {
			await WarnBeforeUndoingScreen.warn(this.type, this.direction, this.transactions.getExternalTransactions());
			await this.transactions.undoAll(this.ticketGlobalId);
		}
	}

	async cancel(afterUndoAll?: () => void): Promise<void> {

		if (this.transactions.hasExternalTransactions()) {
			// OJO: El return es sólo en caso de userConfirms === true. No es igual que unificar las dos condiciones
			// en el if, porque si no, en caso de no haber transacciones externas no se invocaría el afterCancel
			const { nextAction } = await ConfirmCancellationScreen.confirm(this.direction, this.transactions.getExternalTransactions());
			if (nextAction !== 'continue-with-cancellation')
				return;
		}

		await this.transactions.undoAll(this.ticketGlobalId);
		afterUndoAll?.();
	}

	async commitCashPayments(changeAmount: number): Promise<Result> {

		if (!SessionProvider.getCurrent().UseCashControl)
			return Result.ok();

		return await this.executeCashPayments(changeAmount);
	}

	private async executeCashPayments(changeAmount: number): Promise<Result> {

		const cashPayments = this.payments.filter(x => x.IsCash);
		const cashPaymentAmount = cashPayments.sum(x => x.Amount);
		const amountToPay = Math.round(cashPaymentAmount - changeAmount, Currency.DECIMAL_PLACES);

		if (amountToPay === 0)
			return Result.ok();

		const result = await this.transactions.addCashTransaction(this.ticketGlobalId, { amount: this.direction === 'PAY' ? amountToPay : -amountToPay, type: this.type });
		if (!result.ok)
			return Result.error();

		// Si no sólo estoy dando cambio, sino que también estoy cobrando parte de efectivo, tengo que actualizar la
		// cantidad entregada realmente. Para ello, imputo la diferencia al último pago en efectivo.
		if (cashPaymentAmount !== 0) {
			const paid = this.direction === 'PAY' ? result.value.paid : -result.value.paid;
			const delta = Math.round(paid - cashPaymentAmount, Currency.DECIMAL_PLACES);
			cashPayments.last()!.Amount += delta;
		}

		return Result.ok();
	}

	private getAmountToProcess(payment: PaymentDTO): number {

		const amountWithTip = payment.Amount + payment.TipAmount;

		const amountToProcess = this.direction === 'PAY' ? amountWithTip : -amountWithTip;

		if (this.direction === 'PAY' && amountToProcess <= 0)
			throw Error('No se puede realizar un pago con importe menor o igual que 0');

		if (this.direction === 'REFUND' && amountToProcess >= 0)
			throw Error('No se pueden realizar devoluciones con importe mayor o igual que 0');

		return Math.round(amountToProcess, Currency.DECIMAL_PLACES);
	}
}

